import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { User, Tv2, Wallet, Sparkles, Lightbulb, Bookmark, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import FeedCard, { fmtDate, MediaIcon, cardProjectId, detailLink } from './cards/FeedCard';

const DEFAULT_API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const INTENT_LABEL = {
  title_discovery: 'Title discovery',
  person_discovery: 'People',
  availability_lookup: 'Where to watch',
  release_lookup: 'Upcoming releases',
  event_lookup: 'Development events',
  trend_lookup: 'Trending',
  subscription_optimisation: 'Subscription advice',
  creator_research: 'Creator research',
  insider_intelligence: 'Insider intelligence',
  general_entertainment_question: 'Entertainment',
};

// Filter chips, in display order, keyed by result type.
const FILTERS = [
  ['title', 'Titles'],
  ['person', 'People'],
  ['release', 'Releases'],
  ['event', 'Events'],
  ['trend', 'Trending'],
  ['creator_opportunity', 'Creator'],
  ['availability', 'Streaming'],
  ['subscription_advice', 'Subscription'],
];

// Watchlist toggle overlaid on a card. Stops the click from triggering the
// card's link. Watchlisting a title adds its releases to the user's calendar.
function WatchlistChip({ onWatchlist, onToggle }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle(); }}
      title={onWatchlist ? 'On your watchlist — its releases show in your calendar' : 'Add to watchlist — its releases show in your calendar'}
      className={`absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium transition-colors ${
        onWatchlist ? 'bg-videbits-100 text-videbits-700 hover:bg-videbits-200' : 'bg-videbits-50 text-videbits-700 hover:bg-videbits-100'
      }`}
    >
      {onWatchlist ? <><Check className="w-3 h-3" />On watchlist</> : <><Bookmark className="w-3 h-3" />Watchlist</>}
    </button>
  );
}

function ResultCard({ r, authed, isFollowed, onToggleFollow }) {
  const pid = cardProjectId(r);
  const follow = authed && pid && r.type !== 'title' && r.type !== 'person' ? (
    <WatchlistChip onWatchlist={isFollowed(pid)} onToggle={() => onToggleFollow(pid)} />
  ) : null;
  switch (r.type) {
    case 'title':
      return (
        <Link to={detailLink(r)} className="block bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-1"><MediaIcon mediaType={r.media_type} className="w-3.5 h-3.5" /> Title{r.status ? ` · ${r.status}` : ''}</div>
          <div className="font-medium text-gray-900">{r.title}</div>
          <div className="text-xs text-gray-500 mt-1">{r.reason}</div>
        </Link>
      );
    case 'person':
      return (
        <Link to={`/?q=${encodeURIComponent(r.name)}`} className="block bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-1"><User className="w-3.5 h-3.5" /> Person</div>
          <div className="font-medium text-gray-900">{r.name}</div>
          {r.known_for && <div className="text-xs text-gray-500 mt-1">Known for {r.known_for}</div>}
        </Link>
      );
    case 'release':
    case 'event':
    case 'trend':
      return <FeedCard r={r} follow={follow} />;
    case 'creator_opportunity':
      return (
        <Link to={detailLink(r)} className="relative block bg-amber-50 border border-amber-200 rounded-xl p-4 hover:shadow-md transition-shadow">
          {follow}
          <div className="flex items-center gap-2 text-xs text-amber-700 mb-1"><Lightbulb className="w-3.5 h-3.5" /> Creator opportunity{typeof r.percentage_change === 'number' ? ` · +${Math.round(r.percentage_change)}%` : ''}</div>
          <div className="font-medium text-gray-900 pr-20">{r.title}</div>
          <div className="text-sm text-gray-700 mt-1">{r.angle}</div>
          {r.upcoming_date && <div className="text-xs text-gray-500 mt-1">{(r.upcoming_event || '').replace(/_/g, ' ').toLowerCase()} · {fmtDate(r.upcoming_date)}</div>}
        </Link>
      );
    case 'availability':
      return (
        <Link to={detailLink(r)} className="relative block bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
          {follow}
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-1"><Tv2 className="w-3.5 h-3.5" /> {r.country} · {r.offer_type}</div>
          <div className="font-medium text-gray-900 pr-20">{r.title}</div>
          <div className="text-xs text-gray-500 mt-1">{r.reason}</div>
        </Link>
      );
    case 'subscription_advice':
      return (
        <div className="bg-videbits-50 border border-videbits-200 rounded-xl p-4">
          <div className="flex items-center gap-2 text-xs text-videbits-700 mb-1"><Wallet className="w-3.5 h-3.5" /> Subscription advice</div>
          <div className="font-medium text-gray-900">{r.recommendation}</div>
          <div className="text-xs text-gray-600 mt-1">{r.reason}</div>
          {typeof r.saving === 'number' && r.saving > 0 && (
            <div className="text-xs text-green-700 font-medium mt-1">Save ~{r.saving}/mo</div>
          )}
          <Link to={r.link || '/optimiser'} className="inline-block mt-2 text-sm font-medium text-videbits-700 hover:text-videbits-900">Open optimiser →</Link>
        </div>
      );
    default:
      return null;
  }
}

// Intents fully answered by the catalogue results — in augment mode the AI panel
// stays silent for these so plain title searches aren't cluttered.
const PURE_CATALOGUE_INTENTS = new Set(['title_discovery', 'general_entertainment_question']);

// Runs the intelligence search and renders mixed result cards (releases, events,
// trends, availability, subscription advice, summary) with type filters.
// `augment`: render above the catalogue results — hide title cards and stay
// silent for plain catalogue searches.
export default function IntelligentResults({ query, apiBase = DEFAULT_API, augment = false }) {
  const { token, isAuthenticated } = useAuth();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [followed, setFollowed] = useState(new Set());

  // Load the user's title follows so cards can show follow state (only when
  // there's a query to render results for).
  useEffect(() => {
    if (!token || !query || !query.trim()) { setFollowed(new Set()); return undefined; }
    let active = true;
    fetch(`${apiBase}/api/watchlist`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : { follows: [] }))
      .then((d) => {
        if (active) setFollowed(new Set((d.follows || []).filter((f) => f.entity_type === 'title').map((f) => f.entity_id)));
      })
      .catch(() => {});
    return () => { active = false; };
  }, [token, apiBase, query]);

  const toggleFollow = useCallback(async (projectId) => {
    if (!projectId) return;
    const isF = followed.has(projectId);
    setFollowed((prev) => {
      const n = new Set(prev);
      if (isF) n.delete(projectId); else n.add(projectId);
      return n;
    });
    const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
    try {
      if (isF) {
        await fetch(`${apiBase}/api/watchlist/title/${projectId}`, { method: 'DELETE', headers });
      } else {
        await fetch(`${apiBase}/api/watchlist`, { method: 'POST', headers, body: JSON.stringify({ entity_type: 'title', entity_id: projectId }) });
      }
    } catch {
      // revert on failure
      setFollowed((prev) => {
        const n = new Set(prev);
        if (isF) n.add(projectId); else n.delete(projectId);
        return n;
      });
    }
  }, [followed, token, apiBase]);

  const run = useCallback(async () => {
    if (!query || !query.trim()) { setData(null); return; }
    setIsLoading(true);
    try {
      const r = await fetch(`${apiBase}/api/search/intelligent?q=${encodeURIComponent(query)}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!r.ok) throw new Error(`Request failed (${r.status})`);
      setData(await r.json());
    } catch {
      setData({ results: [], detected_intent: null });
    } finally {
      setIsLoading(false);
    }
  }, [query, apiBase, token]);

  useEffect(() => { run(); }, [run]);

  // In augment mode this sits above the catalogue results, so it stays quiet
  // while loading and on plain catalogue searches (the catalogue answers those).
  if (isLoading) {
    return augment ? null : <div className="max-w-4xl mx-auto text-center text-gray-400 py-12">Thinking…</div>;
  }
  if (!data) return null;
  if (augment && PURE_CATALOGUE_INTENTS.has(data.detected_intent)) return null;

  const all = data.results || [];
  // The intelligence summary leads full-width; the rest go in the filtered grid.
  const summary = all.find((r) => r.type === 'intelligence_summary');
  // Augment mode hides title cards — the catalogue results render those richly.
  const results = all.filter((r) => r.type !== 'intelligence_summary' && !(augment && r.type === 'title'));
  if (augment && !summary && results.length === 0) return null;
  const present = new Set(results.map((r) => r.type));
  const shown = filter === 'all' ? results : results.filter((r) => r.type === filter);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center gap-2 mb-4 text-sm text-gray-500">
        <Sparkles className="w-4 h-4 text-videbits-500" />
        <span>Understood as <span className="font-medium text-gray-700">{INTENT_LABEL[data.detected_intent] || 'Entertainment'}</span></span>
      </div>

      {summary && (
        <div className="bg-gradient-to-br from-videbits-50 to-white border border-videbits-200 rounded-xl p-4 mb-5">
          <div className="flex items-center gap-2 text-xs font-medium text-videbits-700 mb-1"><Sparkles className="w-3.5 h-3.5" /> Intelligence summary</div>
          <p className="text-sm text-gray-800 leading-relaxed">{summary.summary}</p>
        </div>
      )}

      {data.note && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-800">{data.note}</div>
      )}

      {results.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5">
          <button onClick={() => setFilter('all')} className={`px-3 py-1 rounded-full text-sm font-medium ${filter === 'all' ? 'bg-videbits-600 text-white' : 'bg-videbits-50 text-videbits-700 hover:bg-videbits-100'}`}>All</button>
          {FILTERS.filter(([k]) => present.has(k)).map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1 rounded-full text-sm font-medium ${filter === k ? 'bg-videbits-600 text-white' : 'bg-videbits-50 text-videbits-700 hover:bg-videbits-100'}`}>{label}</button>
          ))}
        </div>
      )}

      {shown.length === 0 ? (
        <p className="text-gray-400 text-sm py-8">No intelligence results for that query yet.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {shown.map((r, i) => (
            <ResultCard
              key={`${r.type}:${r.id || r.project_id || r.entity_id || i}`}
              r={r}
              authed={isAuthenticated}
              isFollowed={(id) => followed.has(id)}
              onToggleFollow={toggleFollow}
            />
          ))}
        </div>
      )}
    </div>
  );
}
